import axios from 'axios';
import { safeLog, sanitizeForLogging } from '../utils/security';

// Create axios instance with base configuration
const agentAPI = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
agentAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const projectId = localStorage.getItem('currentProjectId');
    if (projectId) config.headers['X-Project-Id'] = projectId;

    // Log request in development (with sanitized data)
    if (process.env.NODE_ENV === 'development') {
      safeLog.log('🚀 Agent API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: sanitizeForLogging(config.data)
      });
    }

    return config;
  },
  (error) => {
    safeLog.error('❌ Agent API Request Error:', sanitizeForLogging(error));
    return Promise.reject(error);
  }
);

// Response interceptor for logging and error handling
agentAPI.interceptors.response.use(
  (response) => {
    // Log response in development (with sanitized data)
    if (process.env.NODE_ENV === 'development') {
      safeLog.log('✅ Agent API Response:', {
        status: response.status,
        url: response.config.url,
        data: sanitizeForLogging(response.data)
      });
    }

    return response;
  },
  (error) => {
    const sanitizedError = sanitizeForLogging(error.response?.data || error.message);
    safeLog.error('❌ Agent API Response Error:', sanitizedError);

    // Handle common error scenarios
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/signin';
    }

    return Promise.reject(error);
  }
);

const agentService = {
  /**
   * Create a new agent (Step 1: Agent Configuration)
   * @param {Object} agentData - Agent configuration data
   * @returns {Promise<Object>} API response
   */
  async createAgent(agentData) {
    try {
      const response = await agentAPI.post('/agents', {
        name: agentData.name,
        description: agentData.description
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to create agent');
    }
  },

  /**
   * Update agent basic information (name, description)
   * @param {string} agentId - Agent ID
   * @param {Object} agentData - Agent data to update
   * @returns {Promise<Object>} API response
   */
  async updateAgent(agentId, agentData) {
    try {
      const response = await agentAPI.put(`/agents/${agentId}`, {
        name: agentData.name,
        description: agentData.description
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to update agent');
    }
  },

  /**
   * Update agent with product information (Step 2)
   * @param {string} agentId - Agent ID
   * @param {Object} productData - Product information data
   * @returns {Promise<Object>} API response
   */
  async updateAgentProduct(agentId, productData) {
    try {
      const response = await agentAPI.put(`/agents/${agentId}/product`, {
        description: productData.description,
        url: productData.url
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to update product information');
    }
  },

  /**
   * Update agent with ICP configuration (Step 3)
   * @param {string} agentId - Agent ID
   * @param {Object} icpData - ICP configuration data
   * @returns {Promise<Object>} API response
   */
  async updateAgentICP(agentId, icpData) {
    try {
      const response = await agentAPI.put(`/agents/${agentId}/icp`, {
        // Standard Fields
        jobTitles: icpData.jobTitles,
        companies: icpData.companies,
        locations: icpData.locations,
        revenue: {
          type: icpData.revenueType,
          min: icpData.revenueMin,
          max: icpData.revenueMax
        },
        employeeRange: icpData.employeeRange,
        fundingStages: icpData.fundingStages,
        companyTypes: icpData.companyTypes,

        // Job Title Settings
        jobTitleSettings: {
          tab: icpData.jobTitlesTab,
          includeSimilarTitles: icpData.includeSimilarTitles
        },
        messagingStyle: icpData.messagingStyle,

        // --- NEW FIELDS ---
        seniority: icpData.seniority,
        jobFunction: icpData.jobFunction,
        emailStatus: icpData.emailStatus,
        industry: icpData.industry,

        // Numerical objects need to be passed as objects
        estimatedNumEmployees: icpData.estimatedNumEmployees,
        lastFundingDate: icpData.lastFundingDate,
        foundedYear: icpData.foundedYear,
        fundingAmount: icpData.fundingAmount,

        // Arrays
        technologies: icpData.technologies,
        keywords: icpData.keywords,
        employeeGrowth12mo: icpData.employeeGrowth12mo
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to update ICP configuration');
    }
  },

  /**
   * Lookup dynamic options for autocomplete
   * @param {string} type - Type of data (job_title, company, etc.)
   * @param {string} query - Search query
   * @returns {Promise<Array>} List of suggestions
   */
  async lookupOptions(type, query) {
    try {
      const response = await agentAPI.get('/agents/lookup', {
        params: { type, q: query }
      });
      return response.data;
    } catch (error) {
      console.warn('Lookup failed, returning empty suggestions', error);
      return { data: [] }; // Fail gracefully
    }
  },

  /**
   * Activate an agent (Final step)
   * @param {string} agentId - Agent ID
   * @returns {Promise<Object>} API response
   */
  /** Create the ready-made 4-node campaign for this agent. */
  async createDefaultCampaign(agentId) {
    const response = await agentAPI.post(`/agents/${agentId}/default-campaign`);
    return response.data.data.workflow;
  },

  async activateAgent(agentId) {
    try {
      const response = await agentAPI.put(`/agents/${agentId}/activate`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to activate agent');
    }
  },

  /**
   * Get all agents for the current user
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} API response
   */
  async getUserAgents(filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.status) {
        params.append('status', filters.status);
      }

      if (filters.active !== undefined) {
        params.append('active', filters.active.toString());
      }

      const response = await agentAPI.get(`/agents?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch agents');
    }
  },

  /**
   * Get a single agent by ID
   * @param {string} agentId - Agent ID
   * @returns {Promise<Object>} API response
   */
  async getAgentById(agentId) {
    try {
      const response = await agentAPI.get(`/agents/${agentId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch agent');
    }
  },

  /**
   * Delete an agent
   * @param {string} agentId - Agent ID
   * @returns {Promise<Object>} API response
   */
  async deleteAgent(agentId) {
    try {
      const response = await agentAPI.delete(`/agents/${agentId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to delete agent');
    }
  },

  /**
   * Upload Lead File (CSV/Excel)
   * @param {string} agentId - Agent ID
   * @param {File} file - File object
   * @param {Function} onUploadProgress - Progress callback
   * @returns {Promise<Object>} API response
   */
  async uploadLeadFile(agentId, file, onUploadProgress) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await agentAPI.post(`/agents/${agentId}/leads`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to upload lead file');
    }
  },

  /**
   * How Orion proposes to read an uploaded file. Imports nothing, so it is
   * safe to call before the user has decided.
   * @param {string} agentId - Agent ID
   * @param {string} fileKey - Which uploaded file; the latest if omitted
   * @returns {Promise<Object>} API response
   */
  async previewLeadFile(agentId, fileKey) {
    try {
      const response = await agentAPI.post(`/agents/${agentId}/leads/preview`, { fileKey });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to read that file');
    }
  },

  /**
   * Import an uploaded file into Leads using the confirmed mapping
   * @param {string} agentId - Agent ID
   * @param {string} fileKey - Which uploaded file
   * @param {Object} mapping - Column header to field
   * @returns {Promise<Object>} API response
   */
  async importLeadFile(agentId, fileKey, mapping) {
    try {
      const response = await agentAPI.post(`/agents/${agentId}/leads/import`, { fileKey, mapping });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to import that file');
    }
  },

  /**
   * Handle API errors consistently
   * @param {Error} error - The error object
   * @param {string} defaultMessage - Default error message
   * @returns {Error} Formatted error
   */
  handleError(error, defaultMessage) {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    } else if (error.response?.data?.errors) {
      // Handle validation errors
      const validationErrors = error.response.data.errors
        .map(err => err.msg)
        .join(', ');
      return new Error(`Validation failed: ${validationErrors}`);
    } else if (error.message) {
      return new Error(error.message);
    } else {
      return new Error(defaultMessage);
    }
  }
};

export default agentService;
